import { useState, useEffect, useCallback, useRef, createContext, useContext, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { UserConfig, ConfigNode } from '../types';
import { useAuth } from './useAuth';
import { useMenu } from './useMenu';
import { updateConfigMap } from '../lib/dbHelper';
import { DEFAULT_CONFIG } from '../constants/defaultConfig';
import { cache } from '../lib/cache';

const MAX_RETRIES = 5;
const RETRY_DELAY = 2000;

const mergeConfig = (dbConfig: any, defaultConfig: UserConfig, menuItems: any[]): UserConfig => {
    const merged = dbConfig && dbConfig.items ? JSON.parse(JSON.stringify(dbConfig)) : JSON.parse(JSON.stringify(defaultConfig));
  
  if (!menuItems || menuItems.length === 0) return merged;

  const items = merged.items as ConfigNode[];
  
  // Assicuriamoci che "Aspetto" e "File System" esistano nel corretto ordine
  const requiredRoots = ['Aspetto', 'File System'];
  requiredRoots.forEach(rootName => {
    if (!items.find(it => it.item === rootName)) {
      const defRoot = defaultConfig.items.find(it => it.item === rootName);
      if (defRoot) items.push(JSON.parse(JSON.stringify(defRoot)));
    }
  });

  // Rimuoviamo "Io" come radice se presente (lo gestiremo dentro Voci Menù)
  const ioIdx = items.findIndex(it => it.item === 'Io');
  if (ioIdx > -1) items.splice(ioIdx, 1);

  const aspetto = items.find(it => it.item === 'Aspetto');
  if (!aspetto!.children) aspetto!.children = [];

  let menuSection = aspetto!.children.find(c => c.item === 'Menù');
  if (!menuSection) {
    const defMenuSection = defaultConfig.items.find(it => it.item === 'Aspetto')?.children?.find(c => c.item === 'Menù');
    if (defMenuSection) {
      menuSection = JSON.parse(JSON.stringify(defMenuSection));
      aspetto!.children.push(menuSection!);
    } else {
      menuSection = { item: 'Menù', children: [] };
      aspetto!.children.push(menuSection);
    }
  }

  if (!menuSection.children) menuSection.children = [];

  // Pulizia: rimuoviamo "Voci Menù" se presente e portiamo le sezioni Colori, Dimensioni, Font al livello superiore di Menù
  const vociMenuIdx = menuSection.children.findIndex(c => c.item === 'Voci Menù');
  if (vociMenuIdx > -1) {
    const vociMenuNode = menuSection.children[vociMenuIdx];
    const defaultNode = vociMenuNode.children?.find(c => c.item === 'Default');
    if (defaultNode && defaultNode.children) {
      // Distribuiamo Colori, Dimensioni, Font se non esistono già
      defaultNode.children.forEach(styleNode => {
        if (!menuSection!.children!.some(c => c.item === styleNode.item)) {
          menuSection!.children!.push(styleNode);
        }
      });
    }
    menuSection.children.splice(vociMenuIdx, 1);
  }

  // Assicuriamoci che Colori, Dimensioni, Font, Icone, Sezioni esistano e abbiano tutti i parametri
  ['Colori', 'Dimensioni', 'Font', 'Icone', 'Sezioni'].forEach(styleName => {
    let existingNode = menuSection!.children!.find(c => c.item === styleName);
    const defAspetto = defaultConfig.items.find(it => it.item === 'Aspetto');
    const defMenu = defAspetto?.children?.find(c => c.item === 'Menù');
    const defNode = defMenu?.children?.find(c => c.item === styleName);

    if (!existingNode) {
      if (defNode) menuSection!.children!.push(JSON.parse(JSON.stringify(defNode)));
    } else if (defNode) {
      // Merge parameters if node exists but is missing some keys
      Object.keys(defNode).forEach(key => {
        if (key !== 'item' && key !== 'children' && !existingNode![key]) {
          existingNode![key] = JSON.parse(JSON.stringify(defNode[key]));
        }
      });
    }
  });

  return merged;
};

interface ConfigContextType {
  config: UserConfig;
  loading: boolean;
  error: any;
  refreshConfig: () => Promise<void>;
  updateConfig: (newConfig: UserConfig) => Promise<boolean>;
  setPreviewConfig: (newConfig: UserConfig) => void;
  getTileStyles: (itemName?: string) => any;
  realtimeStatus: 'connecting' | 'connected' | 'error' | 'disabled';
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading, isProcessing: authProcessing } = useAuth();
  const { menu, loading: menuLoading } = useMenu();
  const [config, setConfig] = useState<UserConfig>(DEFAULT_CONFIG);

  // Sincronizziamo il map globale dbHelper ogni volta che cambia lo stato locale della config
  useEffect(() => {
    updateConfigMap(config);
  }, [config]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  // Usiamo ref per tutto ciò che non deve causare re-render o re-esecuzione di useEffect
  const retryCountRef = useRef(0);
  const userIdRef = useRef<string | null>(null);
  const pgrstErrorLoggedRef = useRef(false);
  const channelRef = useRef<any>(null);
  const isSubscribedRef = useRef(false);
  const realtimeRetryCountRef = useRef(0);
  const isRealtimePermanentlyDisabledRef = useRef(false);
  const [realtimeStatus, setRealtimeStatus] = useState<'connecting' | 'connected' | 'error' | 'disabled'>('connecting');

  const fetchConfig = useCallback(async (isRetry = false, invalidateCache = false) => {
    if (!userIdRef.current) return;
    
    if (!isRetry) {
      retryCountRef.current = 0;
      setLoading(true);
    }

    if (!invalidateCache && !isRetry) {
      const cachedConfig = cache.get<UserConfig>(`config_${userIdRef.current}`);
      if (cachedConfig) {
        setConfig(cachedConfig);
        setLoading(false);
        return;
      }
    }

    if (!pgrstErrorLoggedRef.current || !isRetry) {
    }
    
    try {
      const { data, error } = await supabase
        .from('cpgev_config')
        .select('data')
        .eq('id', userIdRef.current)
        .single();


      if (error) {
        if (error.code === 'PGRST116') {
          const { error: insertError } = await supabase
            .from('cpgev_config')
            .insert({ id: userIdRef.current, data: DEFAULT_CONFIG });
          
          if (!insertError) {
            setConfig(prev => JSON.stringify(prev) === JSON.stringify(DEFAULT_CONFIG) ? prev : DEFAULT_CONFIG);
          } else {
            console.error('Error creating config in cpgev_config:', insertError);
            setConfig(prev => JSON.stringify(prev) === JSON.stringify(DEFAULT_CONFIG) ? prev : DEFAULT_CONFIG);
          }
        } else {
          // Se è un errore di schema o di connessione, lo lanciamo per attivare il retry nel catch
          throw error;
        }
      } else if (data) {
        const merged = mergeConfig(data.data, DEFAULT_CONFIG, menu);
        setConfig(prev => JSON.stringify(prev) === JSON.stringify(merged) ? prev : merged);
        cache.set(`config_${userIdRef.current}`, merged);
      } else {
        const merged = mergeConfig(null, DEFAULT_CONFIG, menu);
        setConfig(merged);
      }
      
      setError(null);
      pgrstErrorLoggedRef.current = false;
      retryCountRef.current = 0;
      setLoading(false);
    } catch (err: any) {
      const isPgrstError = err?.code === 'PGRST002';
      
      if (isPgrstError && !pgrstErrorLoggedRef.current) {
        console.warn('useConfig: Errore PGRST002 (schema cache). Tentativo di ricaricamento automatico...');
        pgrstErrorLoggedRef.current = true;
      }

      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        const delay = RETRY_DELAY * Math.pow(1.5, retryCountRef.current - 1);
        setTimeout(() => fetchConfig(true, invalidateCache), delay);
      } else {
        console.error('useConfig: Massimo tentativi raggiunto o errore non recuperabile:', err);
        setError(err);
        const merged = mergeConfig(null, DEFAULT_CONFIG, menu);
        setConfig(merged);
        setLoading(false);
      }
    }
  }, [menu]);

  useEffect(() => {
    const isAuthLoading = authLoading || authProcessing;

    if (!isAuthLoading && user?.id && !menuLoading) {
      if (userIdRef.current === user.id && !loading && config?.items?.length > 0) {
        return;
      }
      userIdRef.current = user.id;
      fetchConfig();
    } else if (!isAuthLoading && !user) {
      setConfig(DEFAULT_CONFIG);
      setLoading(false);
      userIdRef.current = null;
    }
  }, [authLoading, authProcessing, user?.id, fetchConfig, menuLoading]);

  const updateConfig = async (newConfig: UserConfig) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('cpgev_config')
        .upsert({ id: user.id, data: newConfig });

      if (error) throw error;
      setConfig(newConfig);
      cache.set(`config_${user.id}`, newConfig);
      return true;
    } catch (err) {
      console.error('Error updating config in cpgev_config:', err);
      return false;
    }
  };

  const setPreviewConfig = useCallback((newConfig: UserConfig | null) => {
    if (newConfig && newConfig.items) {
      setConfig(newConfig);
    }
  }, []);

  const getTileStyles = useCallback((itemName?: string) => {
    const configToUse = (config && config.items) ? config : DEFAULT_CONFIG;
    
    const findNodeByName = (nodes: ConfigNode[], name: string): ConfigNode | null => {
      if (!nodes || !Array.isArray(nodes)) return null;
      for (const node of nodes) {
        if (node.item?.toLowerCase() === name.toLowerCase()) return node;
        if (node.children) {
          const found = findNodeByName(node.children, name);
          if (found) return found;
        }
      }
      return null;
    };

    const getFieldFromNode = (node: ConfigNode | null, fieldName: string) => {
      if (!node) return null;
      if (node[fieldName] && Array.isArray(node[fieldName])) return node[fieldName][0];
      if (node.children) {
        for (const child of node.children) {
          if (child[fieldName] && Array.isArray(child[fieldName])) return child[fieldName][0];
        }
      }
      return null;
    };

    const menusNode = findNodeByName(configToUse.items || [], "Menù");
    const coloriNode = menusNode?.children?.find(c => c.item === 'Colori');
    const dimensioniNode = menusNode?.children?.find(c => c.item === 'Dimensioni');
    const fontNode = menusNode?.children?.find(c => c.item === 'Font');
    const iconeNode = menusNode?.children?.find(c => c.item === 'Icone');
    const sezioniNode = menusNode?.children?.find(c => c.item === 'Sezioni');

    const getValue = (fieldName: string, fallback: string) => {
      // Priorità 1: Look nel nodo specifico (Colori, Dimensioni, o Font)
      let targetNode = null;
      if (['sfondo', 'voci', 'mouseover', 'onclick', 'selected', 'unselected'].includes(fieldName)) {
        targetNode = coloriNode;
      } else if (['margini', 'padding'].includes(fieldName)) {
        targetNode = dimensioniNode;
      } else if (['tipo_carattere', 'dimensione', 'formato', 'spaziatura'].includes(fieldName)) {
        targetNode = fontNode;
      } else if (['colore_icona', 'sfondo_icona', 'bordo_icona_colore', 'bordo_icona_spessore'].includes(fieldName)) {
        targetNode = iconeNode;
      } else if (fieldName.includes('desktop_') || fieldName.includes('tablet_') || fieldName.includes('mobile_') || fieldName === 'bordo_spessore' || fieldName === 'bordo_colore') {
        const isMenuBordo = ['bordo_tipo', 'bordo_spessore', 'bordo_colore'].includes(fieldName);
        if (!isMenuBordo || fieldName === 'bordo_spessore' || fieldName === 'bordo_colore') {
          targetNode = sezioniNode;
        }
      }

      const field = getFieldFromNode(targetNode, fieldName);
      if (field && field.userValue !== undefined && field.userValue !== null && field.userValue !== "") {
        return field.userValue;
      }

      // Priorità 2: fallback sulla sezione Menù generale per Sfondo/Bordo
      if (fieldName === 'sfondo') {
        const sfondoGenNode = menusNode?.children?.find(c => c.item === 'Sfondo');
        const sfondoGenField = getFieldFromNode(sfondoGenNode, 'sfondo_generale');
        if (sfondoGenField?.userValue) return sfondoGenField.userValue;
      }
      if (fieldName.startsWith('bordo_')) {
        const bordoNode = menusNode?.children?.find(c => c.item === 'Bordo');
        const bordoField = getFieldFromNode(bordoNode, fieldName);
        if (bordoField?.userValue) return bordoField.userValue;
      }

      if (field) return field.defaultValue;
      return fallback;
    };

    const fontName = getValue('tipo_carattere', 'Inter');
    const safeFontStack = fontName.includes(' ') && !fontName.includes('"') ? `"${fontName}", sans-serif` : `${fontName}, sans-serif`;

    return {
      sfondo: getValue('sfondo', '#ffffff'),
      voci: getValue('voci', '#111827'),
      mouseover: getValue('mouseover', '#f3f4f6'),
      onclick: getValue('onclick', '#e5e7eb'),
      selected: getValue('selected', '#3b82f6'),
      unselected: getValue('unselected', '#6b7280'),
      margini: getValue('margini', '4px'),
      padding: getValue('padding', '2px'),
      tipo_carattere: safeFontStack,
      dimensione: getValue('dimensione', '13px'),
      formato: getValue('formato', 'normal'),
      spaziatura: getValue('spaziatura', 'normal'),
      folder_download: getValue('folder_download', ''),
      folder_upload: getValue('folder_upload', ''),
      // Nuovi campi per Bordo
      bordo_tipo: getValue('bordo_tipo', 'solid'),
      bordo_spessore: getValue('bordo_spessore', '1px'),
      bordo_colore: getValue('bordo_colore', '#e5e7eb'),
      // Icone
      colore_icona: getValue('colore_icona', '#3b82f6'),
      sfondo_icona: getValue('sfondo_icona', '#eff6ff'),
      bordo_icona_colore: getValue('bordo_icona_colore', '#ffffff'),
      bordo_icona_spessore: getValue('bordo_icona_spessore', '1px'),
      // Sezioni (Responsive Layout & Styles)
      desktop_larghezza: getValue('desktop_larghezza', '200px'),
      desktop_altezza: getValue('desktop_altezza', '110px'),
      desktop_sfondo: getValue('desktop_sfondo', '#ffffff'),
      desktop_font: getValue('desktop_font_titolo', 'Inter'),
      desktop_colore: getValue('desktop_colore_titolo', '#111827'),
      desktop_stile: getValue('desktop_stile_titolo', 'bold'),

      tablet_larghezza: getValue('tablet_larghezza', '180px'),
      tablet_altezza: getValue('tablet_altezza', '100px'),
      tablet_sfondo: getValue('tablet_sfondo', '#ffffff'),
      tablet_font: getValue('tablet_font_titolo', 'Inter'),
      tablet_colore: getValue('tablet_colore_titolo', '#111827'),
      tablet_stile: getValue('tablet_stile_titolo', 'bold'),

      mobile_larghezza: getValue('mobile_larghezza', '100%'),
      mobile_altezza: getValue('mobile_altezza', '80px'),
      mobile_sfondo: getValue('mobile_sfondo', '#ffffff'),
      mobile_font: getValue('mobile_font_titolo', 'Inter'),
      mobile_colore: getValue('mobile_colore_titolo', '#111827'),
      mobile_stile: getValue('mobile_stile_titolo', 'bold'),

      bordo_sezione_spessore: getValue('bordo_spessore', '1px'), 
      bordo_sezione_colore: getValue('bordo_colore', '#e5e7eb'),
      // Icon overrides from config if present
      icon: null,
      iconPath: null,
      // Aliases per compatibilità con componenti grafici (Legacy & New)
      backgroundColor: getValue('sfondo', '#ffffff'),
      borderColor: getValue('selected', '#3b82f6'),
      borderSize: '1px',
      textColor: getValue('voci', '#111827'),
      textFont: safeFontStack,
      linkTextColor: getValue('selected', '#3b82f6'),
      linkTextFont: safeFontStack,
      margin: getValue('margini', '4px')
    };
  }, [config]);

  const refreshConfig = useCallback(async () => {
    setLoading(true);
    setError(null); // Reset dell'errore per permettere il retry manuale
    pgrstErrorLoggedRef.current = false;
    retryCountRef.current = 0;
    await fetchConfig(false, true);
  }, [fetchConfig]);

  // Sottoscrizione Realtime per la configurazione personale
  useEffect(() => {
    if (authLoading || !user?.id) return;
    if (isSubscribedRef.current) return;
    isSubscribedRef.current = true;

    const setupSubscription = async () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        console.error('useConfig: Errore getSession in setupSubscription:', error);
        isSubscribedRef.current = false;
        return;
      }
      if (!session) {
        isSubscribedRef.current = false;
        return;
      }

      const channelName = `config-changes-${user.id}-${Math.random().toString(36).substring(2, 9)}`;
      channelRef.current = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'cpgev_config',
            filter: `id=eq.${user.id}`
          },
          (payload) => {
            fetchConfig(true);
          }
        )
        .subscribe((status, err) => {
          
          if (status === 'SUBSCRIBED') {
            setRealtimeStatus('connected');
            realtimeRetryCountRef.current = 0;
          }
          
          if (status === 'CHANNEL_ERROR') {
            console.warn('useConfig: Errore del canale Realtime (replica probabilmente non abilitata in DB). Disabilito realtime.');
            setRealtimeStatus('disabled');
            isSubscribedRef.current = true; // Latch
            isRealtimePermanentlyDisabledRef.current = true;
            return;
          }
          
          if (status === 'CLOSED') {
            isSubscribedRef.current = false;
            
            if (realtimeRetryCountRef.current < 10) {
              realtimeRetryCountRef.current++;
              setRealtimeStatus('connecting');
              const delay = Math.min(30000, 1000 * Math.pow(2, realtimeRetryCountRef.current));
              setTimeout(() => {
                if (user?.id) {
                  isSubscribedRef.current = false;
                  setupSubscription();
                }
              }, delay);
            } else {
              setRealtimeStatus('disabled');
            }
          }
        });
    };

    setupSubscription();

    const watchdogInterval = setInterval(() => {
      if (isRealtimePermanentlyDisabledRef.current) return;
      
      const currentStatus = channelRef.current?.state;
      if (!currentStatus || currentStatus !== 'joined') {
        isSubscribedRef.current = false;
        setupSubscription();
      }
    }, 30000);

    return () => {
      clearInterval(watchdogInterval);
      isSubscribedRef.current = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, authLoading, fetchConfig]);

  return (
    <ConfigContext.Provider value={{ config, loading, error, refreshConfig, updateConfig, setPreviewConfig, getTileStyles, realtimeStatus }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
